/**
 * NOTE: The design and copy of our cookie banners has been fixed and approved by our legal team.
 *       Please reach out to one of our product counsels if you need to modify it in any way.
 */

import React, { type JSX } from 'react'
import { t } from '@figma/i18n/translation'
import {
  cookiesAllowAllCookies,
  cookiesConsentManager,
  cookiesCookiesSettings,
  cookiesDismiss,
  cookiesDoNotAllowCookies,
  cookiesExplicitConsentBody,
} from '@figma/i18n-dictionaries/en/web/cookies'
import { tx } from '@web/i18n/react'
import { isInIframe } from '@web/lib/browser'
import { EMPTY_SIZE, useResizeObserver } from '@web/lib/hooks/resize_observer'
import { useAtomValue, useSetAtom } from '@web/lib/jotai'
import platform from '@web/lib/platform'
import { DEFAULT_CONTEXT_CTA_CLICKED_EVENT_NAME } from '@web/o11y/analytics/tracking'
import { TrackingContext, useTrackingReactContext } from '@web/o11y/analytics/tracking_context'
import { Svg } from '@web/ui-platform/svg_deprecated'
import classNames from 'classnames'

import { CtaTrackingDescriptors } from 'lib/analytics/cta_tracking_descriptors'
import initialOptions from 'lib/initial_options'
import { AnchorElementTracked } from 'shared/components/forms/basic_form/basic_form'

import { isMeetDevice } from 'figma_app/lib/fullscreen/fullscreen_integration_helpers'

import {
  cookieBannerHeightAtom,
  CookieExistingPreferences,
  cookieExistingPreferencesAtom,
  setCookiePreferences,
} from './cookie_banner_atoms'
import { ConsentRegionType, ManageCookiesUrl } from './cookie_banner_utils'
import { getGlobalPrivacyControl } from './global_privacy_control'
import { CookieBannerVariant } from './types'

import * as styles from './base_cookie_banner.css'

type Props = {
  dismissIcon: SvgSrc
  PrimaryButton: React.ElementType
  SecondaryButton: React.ElementType
  variant: CookieBannerVariant
  persistentMessage?: JSX.Element
  verticalPadding?: number
}

export function CookieBannerExplicitInner(props: Props & { showBanner: boolean }) {
  const [isShowing, setIsShowing] = React.useState(props.showBanner)
  const { trackEvent } = useTrackingReactContext()
  const bannerContainerElement = React.useRef<HTMLDivElement>(null)
  const setHeight = useSetAtom(cookieBannerHeightAtom)

  const cookieBannerSize = useResizeObserver(bannerContainerElement) ?? EMPTY_SIZE
  const newCookieBannerHeight = isShowing
    ? cookieBannerSize.height + (props.verticalPadding || 0)
    : 0

  React.useEffect(() => {
    setHeight(newCookieBannerHeight)
  }, [newCookieBannerHeight, setHeight])

  const handleClickOptOut = async () => {
    setIsShowing(false)
    await setCookiePreferences({ consentRegion: 'explicit', cookiesEnabled: false })
    trackEvent(DEFAULT_CONTEXT_CTA_CLICKED_EVENT_NAME, {
      trackingDescriptor: CtaTrackingDescriptors.COOKIE_BANNER_EXPLICIT_CONSENT_OPT_OUT,
    })
  }
  const handleClickOptIn = async () => {
    setIsShowing(false)
    await setCookiePreferences({ consentRegion: 'explicit', cookiesEnabled: true })
    trackEvent(DEFAULT_CONTEXT_CTA_CLICKED_EVENT_NAME, {
      trackingDescriptor: CtaTrackingDescriptors.COOKIE_BANNER_EXPLICIT_CONSENT_OPT_IN,
    })
  }
  const handleDismiss = async () => {
    setIsShowing(false)
    await setCookiePreferences({ consentRegion: 'explicit', cookiesEnabled: false })
    trackEvent(DEFAULT_CONTEXT_CTA_CLICKED_EVENT_NAME, {
      trackingDescriptor: CtaTrackingDescriptors.COOKIE_BANNER_EXPLICIT_CONSENT_DISMISS,
    })
  }

  if (!isShowing) {
    return (
      (props.persistentMessage && (
        <TrackingContext name="Cookie Banner Persistent Message">
          {props.persistentMessage}
        </TrackingContext>
      )) ||
      null
    )
  }

  return (
    <div
      className={classNames(
        styles.container,
        props.variant === CookieBannerVariant.COMMUNITY && [
          styles.container__authOrCommunity,
          styles.container__community,
        ],
        props.variant === CookieBannerVariant.FILE_VIEWER && styles.container__fileViewer,
        props.variant === CookieBannerVariant.PROTOTYPE && styles.container__prototype,
        props.variant === CookieBannerVariant.AUTH && [
          styles.container__authOrCommunity,
          styles.container__auth,
        ],
      )}
      role="region"
      ref={bannerContainerElement}
      aria-label={t(cookiesConsentManager)}
      data-testid="cookie-banner"
    >
      <div className={styles.body}>
        {tx(cookiesExplicitConsentBody)}{' '}
        <AnchorElementTracked
          href={ManageCookiesUrl(ConsentRegionType.EXPLICIT)}
          target="_blank"
          rel="noreferrer"
        >
          {tx(cookiesCookiesSettings)}
        </AnchorElementTracked>
      </div>
      <div className={styles.actions}>
        <props.SecondaryButton onClick={handleClickOptOut} data-testid="cookie-opt-out-button">
          {tx(cookiesDoNotAllowCookies)}
        </props.SecondaryButton>
        <props.PrimaryButton onClick={handleClickOptIn} data-testid="cookie-opt-in-button">
          {tx(cookiesAllowAllCookies)}
        </props.PrimaryButton>
      </div>
      <div className={styles.dismiss}>
        <button
          onClick={handleDismiss}
          type="button"
          data-testid="cookie-dismiss-button"
          aria-label={t(cookiesDismiss)}
        >
          <Svg className={styles.dismissIcon} svg={props.dismissIcon} />
        </button>
      </div>
    </div>
  )
}

export function CookieBannerExplicit(props: Props) {
  const hasExistingPreferences = useAtomValue(cookieExistingPreferencesAtom)

  let shouldHideCookieBanner = false

  if (hasExistingPreferences === CookieExistingPreferences.UNLOADED) {
    shouldHideCookieBanner = true
  }

  if (hasExistingPreferences === CookieExistingPreferences.YES && !props.persistentMessage) {
    shouldHideCookieBanner = true
  }

  // Respect the GlobalPrivacyControl browser API
  if (getGlobalPrivacyControl()) {
    shouldHideCookieBanner = true
  }

  // Don't show the cookie banner to logged-in users
  if (initialOptions().user_data?.id) {
    shouldHideCookieBanner = true
  }

  // NEVER show cookie banners if we're in an embed
  if (initialOptions().is_embed || isInIframe()) {
    shouldHideCookieBanner = true
  }

  // On Google Meet devices, we only use essential cookies and clear them when a file is closed
  if (isMeetDevice()) {
    shouldHideCookieBanner = true
  }

  React.useEffect(() => {
    if (!shouldHideCookieBanner && platform.isIpadNative) {
      // Opt out the cookie tracking as we only need "essential" cookies for logged out experience in iPad app.
      setCookiePreferences({ consentRegion: 'explicit', cookiesEnabled: false })
    }
  }, [shouldHideCookieBanner])

  if (shouldHideCookieBanner || platform.isIpadNative) {
    return null
  }

  return (
    <TrackingContext name="Cookie Banner" properties={{ consentRegion: 'explicit' }}>
      <CookieBannerExplicitInner
        {...props}
        showBanner={hasExistingPreferences === CookieExistingPreferences.NO}
      />
    </TrackingContext>
  )
}
